import { memo, useEffect, useState } from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { toast } from "react-toastify";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import { PagerdutyProgress, usePagerdutyStore } from "stores/PagerdutyStore";

import LoaderModal from "layouts/components/LoaderModal";

// -----------------------------------------------------------------------------

const EnvironmentIntegrationsPagerdutyConnect = memo(
  function EnvironmentIntegrationsPagerduty(): JSX.Element {
    const [pagerdutyStore, pagerdutyActions] = usePagerdutyStore();
    const location = useLocation();
    const [errorMessage, setErrorMessage] = useState("");

    //
    const service = "Pagerduty";
    const searchParams = new URLSearchParams(location.search);

    //
    if (pagerdutyStore.progress < PagerdutyProgress.Submitting) {
      const config = searchParams.get("config");

      pagerdutyActions.submitParams(config);
    }

    //
    useEffect(() => {
      switch (pagerdutyStore.progress) {
        case PagerdutyProgress.Success:
          toast.success("Connected to Pagerduty", {
            position: "bottom-center",
          });
          break;

        case PagerdutyProgress.Error:
        case PagerdutyProgress.ErrorPagerdutyParam:
          setErrorMessage(
            `Failed connecting to Pagerduty due to ${
              pagerdutyStore.errorDetail ?? "unknown error"
            }`
          );
          break;
      }
    }, [pagerdutyStore.progress, pagerdutyStore.errorDetail, service]);

    //
    switch (pagerdutyStore.progress) {
      case PagerdutyProgress.Success:
        //
        return (
          <Navigate
            to={`/environment/destinations/pagerduty/${pagerdutyStore.connectionId}/notifications`}
          />
        );

      case PagerdutyProgress.Error:
      case PagerdutyProgress.ErrorPagerdutyParam:
        return (
          <>
            <Alert severity="error" sx={{ flexGrow: 0 }}>
              {errorMessage}
            </Alert>
            <Box sx={{ width: "100%", height: "100%", mt: 2 }}>
              <Typography variant="h5">
                {"There was an error connecting to the integration"}
              </Typography>
              <Button component={Link} to="/environment/destinations">
                {"Go back"}
              </Button>
            </Box>
          </>
        );

      default:
        return <LoaderModal />;
    }
  }
);

export default EnvironmentIntegrationsPagerdutyConnect;
